import React, { useState, useEffect } from "react";
import { Button, Spinner, Badge } from "reactstrap";
import axios from "axios";
import Swal from "sweetalert2";

/**
 * Fotos de un articulo: la principal (la que muestran todas las pantallas) y
 * las adicionales. Todas salen en la Orden de Produccion (PDFOC de ListadoCortes).
 *
 * Back: GET/POST/DELETE /articulos/:id/fotos y POST /articulos/:id/fotos/principal
 * (FileController). Las fotos se suben de inmediato, no esperan a "Guardar".
 */
const URL_ARTICULOS = process.env.REACT_APP_URL_ARTICULOS;

const encabezados = () => ({
  headers: { Authorization: `Bearer: ${localStorage.getItem("app_token")}` },
});
const mensaje = (error, otro) =>
  (error && error.response && error.response.data && error.response.data.message) || otro;

function FotosArticulo({ id, tamano = 90, onCambio }) {
  const [fotos, setFotos] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [trabajando, setTrabajando] = useState(false);
  const inputId = `fotos-articulo-${id}`;

  const actualizar = (lista) => {
    setFotos(lista || []);
    if (onCambio) onCambio(lista || []);
  };

  useEffect(() => {
    setFotos([]);
    if (!id) return;
    let vigente = true;
    setCargando(true);
    axios
      .get(`${URL_ARTICULOS}/${id}/fotos`, encabezados())
      .then((r) => vigente && setFotos(r.data || []))
      .catch(() => {})
      .finally(() => vigente && setCargando(false));
    return () => {
      vigente = false;
    };
  }, [id]);

  function subir(lista) {
    const archivos = Array.from(lista || []);
    if (!archivos.length) return;
    if (archivos.some((a) => !a.type.startsWith("image/"))) {
      Swal.fire("Archivo no válido", "Elige solo imágenes (JPG o PNG)", "warning");
      return;
    }
    const formData = new FormData();
    archivos.forEach((a) => formData.append("files", a));
    setTrabajando(true);
    axios
      .post(`${URL_ARTICULOS}/${id}/fotos`, formData, encabezados())
      .then((r) => actualizar(r.data))
      .catch((error) => Swal.fire("No se pudo subir", mensaje(error, "Intenta de nuevo"), "error"))
      .finally(() => setTrabajando(false));
  }

  async function borrar(foto) {
    const r = await Swal.fire({
      title: "¿Borrar esta foto?",
      text: foto.principal && fotos.length > 1 ? "La siguiente foto quedará como principal." : "",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, borrar",
      cancelButtonText: "No",
    });
    if (!r.value) return;
    setTrabajando(true);
    axios
      .delete(`${URL_ARTICULOS}/${id}/fotos?publicId=${encodeURIComponent(foto.public_id)}`, encabezados())
      .then((res) => actualizar(res.data))
      .catch((error) => Swal.fire("No se pudo borrar", mensaje(error, "Intenta de nuevo"), "error"))
      .finally(() => setTrabajando(false));
  }

  function hacerPrincipal(foto) {
    setTrabajando(true);
    axios
      .post(`${URL_ARTICULOS}/${id}/fotos/principal`, { publicId: foto.public_id }, encabezados())
      .then((res) => actualizar(res.data))
      .catch((error) => Swal.fire("No se pudo cambiar", mensaje(error, "Intenta de nuevo"), "error"))
      .finally(() => setTrabajando(false));
  }

  if (!id) return null;
  const src = (f, t) =>
    `${process.env.REACT_APP_URL_FOTO_CLOUDINARY}/image/upload/${t ? `c_limit,w_${t},h_${t}/` : ""}v${f.version}/${f.public_id}`;

  return (
    <div>
      <div className="d-flex flex-wrap align-items-start" style={{ gap: 8 }}>
        {cargando ? <Spinner size="sm" /> : null}
        {!cargando && !fotos.length ? (
          <span className="text-muted" style={{ fontSize: 12 }}>
            Sin fotos
          </span>
        ) : null}
        {fotos.map((f) => (
          <div key={f.public_id} className="text-center" style={{ width: tamano }}>
            <a href={src(f)} target="_blank" rel="noopener noreferrer">
              <img
                src={src(f, 300)}
                alt="foto"
                style={{
                  width: tamano,
                  height: tamano,
                  objectFit: "contain",
                  borderRadius: 4,
                  border: f.principal ? "2px solid #28a745" : "1px solid #ddd",
                  background: "#fff",
                }}
              />
            </a>
            <div className="d-flex justify-content-center align-items-center" style={{ gap: 2 }}>
              {f.principal ? (
                <Badge color="success">Principal</Badge>
              ) : (
                <Button
                  size="sm"
                  color="link"
                  className="p-0"
                  title="Hacerla la foto principal"
                  disabled={trabajando}
                  onClick={() => hacerPrincipal(f)}
                >
                  <i className="far fa-star" />
                </Button>
              )}
              <Button
                size="sm"
                color="link"
                className="p-0 text-danger ml-1"
                title="Borrar foto"
                disabled={trabajando}
                onClick={() => borrar(f)}
              >
                <i className="fas fa-trash" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <input
        id={inputId}
        type="file"
        accept="image/*"
        multiple
        style={{ display: "none" }}
        onChange={(e) => {
          subir(e.target.files);
          e.target.value = "";
        }}
      />
      <Button
        size="sm"
        color="info"
        className="mt-1"
        disabled={trabajando}
        onClick={() => document.getElementById(inputId).click()}
      >
        {trabajando ? <Spinner size="sm" /> : <i className="fas fa-camera" />} Agregar fotos
      </Button>
    </div>
  );
}

export default FotosArticulo;
